import type {GetServerSideProps, GetServerSidePropsResult} from 'next';
import {z} from 'zod';
import type {CommunityTopic} from '../../core/entities/community/schema';
import {getCommunityTopic} from '../../core/repositories/communityRepository';
import {
  type GetUserDataForAnalyticsResponseType,
  getUserDataForAnalytics,
  getUsersMe,
} from '../../core/repositories/usersMeRepository';
import {validateCookie} from '../../core/usecases/auth/checkOauthTokenExistence';
import {CommunityDetailPage} from '../../features/community/components/CommunityDetailPage';
import {CommunityLayout} from '../../features/community/components/Layout';
import {withHandler} from '../../server/token/withHandler';
import {ScriptForGA} from '../../shared/components/ScriptForGA';
import {UnexpectedError} from '../../utils/unexpectedError';
import type {NextPageWithLayout} from '../_app';

const QueryParameter = z.object({
  id: z.string(),
});
type QueryParameter = z.infer<typeof QueryParameter>;

type Props = {
  userDataForAnalytics?: GetUserDataForAnalyticsResponseType;
  topic: CommunityTopic;
  communityNickname: string;
  isSignedIn: boolean;
  isStudentVerificationRequired: boolean;
};

export const getServerSideProps: GetServerSideProps = withHandler<
  Props,
  'withOptionalSession' | 'withQuery',
  QueryParameter
>(
  {auth: 'optional', queryValidator: QueryParameter},
  async (context): Promise<GetServerSidePropsResult<Props>> => {
    const {id: topicId} = context.validatedQuery;
    if (!topicId) {
      return {
        notFound: true,
      };
    }

    const {cookies} = context.req;

    const getTopicDetailResponse = await getCommunityTopic({
      cookies: context.req.cookies,
      topicId: Number.parseInt(topicId, 10),
    });
    if (!getTopicDetailResponse.ok) {
      if (getTopicDetailResponse.error === 'NotFoundError') {
        return {
          notFound: true,
        };
      }

      throw new UnexpectedError(getTopicDetailResponse.error);
    }

    const {isValid} = validateCookie(cookies);

    // 大学認証未完了ユーザーは未ログイン相当の表示にするため、ニックネームも空にする
    const isSignedIn = isValid && !context.shouldTreatAsLoggedOut;

    if (!isSignedIn) {
      return {
        props: {
          topic: getTopicDetailResponse.value,
          communityNickname: '',
          isSignedIn: false,
          isStudentVerificationRequired: context.isStudentVerificationRequired,
        },
      };
    }

    const usersMeResponse = await getUsersMe({cookies});
    if (!usersMeResponse.ok) {
      return {
        props: {
          topic: getTopicDetailResponse.value,
          communityNickname: '',
          isSignedIn: false,
          isStudentVerificationRequired: context.isStudentVerificationRequired,
        },
      };
    }

    const props: Props = {
      topic: getTopicDetailResponse.value,
      communityNickname: usersMeResponse.value.communityNickname,
      isSignedIn: true,
      isStudentVerificationRequired: context.isStudentVerificationRequired,
    };

    const userDataResponse = await getUserDataForAnalytics({cookies});
    if (!userDataResponse.ok) {
      return {
        props,
      };
    }

    return {
      props: {
        ...props,
        userDataForAnalytics: userDataResponse.value,
      },
    };
  },
);

const CommunityDetail: NextPageWithLayout<Props> = ({
  userDataForAnalytics,
  topic,
  communityNickname,
  isSignedIn,
  isStudentVerificationRequired,
}) => {
  return (
    <>
      <CommunityDetailPage
        topic={topic}
        communityNickname={communityNickname}
        isSignedIn={isSignedIn}
        isStudentVerificationRequired={isStudentVerificationRequired}
      />
      <ScriptForGA userDataForAnalytics={userDataForAnalytics} />
    </>
  );
};

CommunityDetail.getLayout = (page) => <CommunityLayout>{page}</CommunityLayout>;

export default CommunityDetail;
